import React from 'react';

// ============================================
// INPUT COMPONENT
// ============================================
// A flexible input component with labels, validation states, and icons.
// Uses the design system tokens from tailwind.config.js

export type InputSize = 'sm' | 'md' | 'lg';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'required'> {
  label?: string;
  error?: boolean;
  errorMessage?: string;
  hint?: string;
  size?: InputSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  required?: boolean;
}

// Size styles - padding, font size, height
const sizeStyles: Record<InputSize, string> = {
  sm: 'px-3 py-1.5 text-sm h-8',
  md: 'px-3 py-2 text-sm h-10',
  lg: 'px-4 py-2.5 text-base h-12',
};

// Icon size based on input size
const iconSizeStyles: Record<InputSize, string> = {
  sm: 'w-4 h-4',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
};

// Icon padding adjustments
const leftIconPadding: Record<InputSize, string> = {
  sm: 'pl-9',
  md: 'pl-10',
  lg: 'pl-11',
};

const rightIconPadding: Record<InputSize, string> = {
  sm: 'pr-9',
  md: 'pr-10',
  lg: 'pr-11',
};

// Base styles applied to all inputs
const baseStyles = `
  w-full
  border rounded-lg
  bg-surface
  text-neutral-900 placeholder-neutral-400
  transition-colors duration-fast
  focus:outline-none
  disabled:bg-neutral-100 disabled:text-neutral-500 disabled:cursor-not-allowed
`;

// State-specific styles
const defaultStyles = `
  border-neutral-300
  hover:border-neutral-400
  focus:border-neutral-400
`;

const errorStyles = `
  border-danger-500
  focus:border-danger-500
`;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      errorMessage,
      hint,
      size = 'md',
      leftIcon,
      rightIcon,
      fullWidth = true,
      required,
      className = '',
      id,
      ...props
    },
    ref,
  ) => {
    const reactId = React.useId();
    const inputId = id ?? reactId;

    // Combine all styles
    const inputClasses = [
      baseStyles,
      sizeStyles[size],
      error ? errorStyles : defaultStyles,
      leftIcon ? leftIconPadding[size] : '',
      rightIcon ? rightIconPadding[size] : '',
      className,
    ]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    return (
      <div className={`${fullWidth ? 'w-full' : 'inline-block'} relative`}>
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-neutral-700 mb-1.5">
            {label}
            {required && <span className="text-danger-500 ml-0.5">*</span>}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <span
              className={`absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 ${iconSizeStyles[size]}`}
            >
              {leftIcon}
            </span>
          )}
          <input ref={ref} id={inputId} className={inputClasses} {...props} />
          {rightIcon && (
            <span
              className={`absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 ${iconSizeStyles[size]}`}
            >
              {rightIcon}
            </span>
          )}
        </div>
        {error && errorMessage && <p className="mt-1 text-xs text-danger-600">{errorMessage}</p>}
        {hint && !error && (
          <p className="mt-1 text-xs text-neutral-500">{hint}</p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
